import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { fromNow, initials } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Building2, Users, GitBranch, Shield, Plus, Sparkles, SlidersHorizontal, RotateCcw, Zap, Upload, Loader2, Trash2 } from "lucide-react";
import AutomationsTab from "@/components/AutomationsTab";

const ROLES = [
  { id: "owner", label: "Kuruluş Sahibi" }, { id: "admin", label: "Kuruluş Yöneticisi" },
  { id: "manager", label: "Yönetici" }, { id: "employee", label: "Çalışan" },
  { id: "client", label: "Müşteri Portalı" }, { id: "guest", label: "Misafir" },
];

const MODULE_CATALOG = [
  { key: "dashboard", label: "Genel Bakış" },
  { key: "my-work", label: "İşlerim" },
  { key: "tasks", label: "Görevler" },
  { key: "projects", label: "Projeler" },
  { key: "calendar", label: "Takvim" },
  { key: "chat", label: "Sohbet" },
  { key: "time-tracking", label: "Zaman Takibi" },
  { key: "requests", label: "Talepler ve Onaylar" },
  { key: "leave", label: "İzinler" },
  { key: "notes", label: "Notlar" },
  { key: "meetings", label: "Toplantı Raporları" },
  { key: "designs", label: "Tasarım Onayları" },
  { key: "purchasing", label: "Satınalma Onayları" },
  { key: "reports", label: "Raporlar" },
  { key: "settings", label: "Ayarlar" },
];

export default function SettingsPage() {
  const qc = useQueryClient();
  const { currentOrg, can, canManageAccess, user, refresh } = useAuth();
  const [org, setOrg] = useState({ name: currentOrg?.name || "", primary_color: currentOrg?.primary_color || "#4f46e5", currency: currentOrg?.currency || "TRY", logo: currentOrg?.logo || "" });
  const [logoUploading, setLogoUploading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ name: "", email: "", role: "employee" });
  const [accessUser, setAccessUser] = useState(null);
  const [accessMods, setAccessMods] = useState([]);
  const [removeTarget, setRemoveTarget] = useState(null);
  const manageAccess = canManageAccess && canManageAccess();

  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: async () => (await api.get("/org/members")).data });
  const { data: branches = [] } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data });
  const { data: departments = [] } = useQuery({ queryKey: ["departments"], queryFn: async () => (await api.get("/departments")).data });
  const { data: audit = [] } = useQuery({ queryKey: ["audit"], queryFn: async () => (await api.get("/activity", { params: { limit: 40 } })).data });

  useEffect(() => { if (currentOrg) setOrg({ name: currentOrg.name, primary_color: currentOrg.primary_color, currency: currentOrg.currency || "TRY", logo: currentOrg.logo || "" }); }, [currentOrg]);

  const saveOrg = async () => { try { await api.patch("/org", org); toast.success("Kuruluş güncellendi"); await refresh(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOrg((o) => ({ ...o, logo: data.storage_path }));
      toast.success("Logo yüklendi — kaydetmeyi unutmayın");
    } catch { toast.error("Logo yüklenemedi"); }
    finally { setLogoUploading(false); }
  };
  const doInvite = async () => {
    if (!invite.name || !invite.email) { toast.error("Ad ve e-posta gerekli"); return; }
    try { await api.post("/org/members", invite); toast.success("Kullanıcı eklendi (şifre: WorkHub2026!)"); qc.invalidateQueries({ queryKey: ["members"] }); setInviteOpen(false); setInvite({ name: "", email: "", role: "employee" }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const changeRole = async (uid, role) => { await api.patch(`/org/members/${uid}`, { role }); toast.success("Rol güncellendi"); qc.invalidateQueries({ queryKey: ["members"] }); };
  const deleteMember = async () => {
    if (!removeTarget) return;
    try {
      const { data } = await api.delete(`/org/members/${removeTarget.id}`);
      toast.success(data.deleted_account ? "Kullanıcı tamamen silindi" : "Kullanıcı kuruluştan çıkarıldı");
      qc.invalidateQueries({ queryKey: ["members"] });
      setRemoveTarget(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openAccess = (m) => { setAccessUser(m); setAccessMods(m.modules || []); };
  const toggleMod = (key) => setAccessMods((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  const saveAccess = async () => {
    try {
      const mods = accessMods.includes("dashboard") ? accessMods : ["dashboard", ...accessMods];
      await api.patch(`/org/members/${accessUser.id}/modules`, { allowed_modules: mods });
      toast.success("Erişim güncellendi"); qc.invalidateQueries({ queryKey: ["members"] }); setAccessUser(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const resetAccess = async () => {
    try {
      await api.patch(`/org/members/${accessUser.id}/modules`, { allowed_modules: null });
      toast.success("Rol varsayılanına döndürüldü"); qc.invalidateQueries({ queryKey: ["members"] }); setAccessUser(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addBranch = async () => { const name = prompt("Şube adı:"); if (!name) return; await api.post("/branches", { name }); qc.invalidateQueries({ queryKey: ["branches"] }); };
  const addDept = async () => { const name = prompt("Departman adı:"); if (!name) return; await api.post("/departments", { name }); qc.invalidateQueries({ queryKey: ["departments"] }); };

  return (
    <div>
      <PageHeader title="Ayarlar" subtitle="Kuruluş ve sistem ayarları" />
      <Tabs defaultValue="org">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="org" data-testid="set-tab-org"><Building2 className="h-4 w-4 mr-1.5" /> Kuruluş</TabsTrigger>
          <TabsTrigger value="users" data-testid="set-tab-users"><Users className="h-4 w-4 mr-1.5" /> Kullanıcılar</TabsTrigger>
          <TabsTrigger value="structure" data-testid="set-tab-structure"><GitBranch className="h-4 w-4 mr-1.5" /> Yapı</TabsTrigger>
          <TabsTrigger value="ai" data-testid="set-tab-ai"><Sparkles className="h-4 w-4 mr-1.5" /> AI</TabsTrigger>
          {can("manage_workflows") && <TabsTrigger value="automations" data-testid="set-tab-automations"><Zap className="h-4 w-4 mr-1.5" /> Otomasyonlar</TabsTrigger>}
          <TabsTrigger value="audit" data-testid="set-tab-audit"><Shield className="h-4 w-4 mr-1.5" /> Denetim</TabsTrigger>
        </TabsList>

        <TabsContent value="org" className="mt-4">
          <Card className="p-5 max-w-lg space-y-4">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-xl grid place-items-center overflow-hidden shrink-0" style={{ backgroundColor: org.primary_color }}>
                {org.logo ? <img src={fileUrl(org.logo)} alt="" className="h-full w-full object-cover" /> : <Building2 className="h-7 w-7 text-white" />}
              </div>
              <div>
                <Label>Logo</Label>
                <label className="mt-1.5 block">
                  <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer hover:bg-muted transition-colors text-sm" data-testid="org-logo-btn">
                    {logoUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {org.logo ? "Logoyu Değiştir" : "Logo Yükle"}
                  </span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="org-logo-input" />
                </label>
              </div>
            </div>
            <div><Label>Kuruluş Adı</Label><Input value={org.name} onChange={(e) => setOrg({ ...org, name: e.target.value })} className="mt-1.5" data-testid="org-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ana Renk</Label><div className="flex gap-2 mt-1.5"><Input type="color" value={org.primary_color} onChange={(e) => setOrg({ ...org, primary_color: e.target.value })} className="w-16 h-10 p-1" data-testid="org-color-input" /><Input value={org.primary_color} onChange={(e) => setOrg({ ...org, primary_color: e.target.value })} /></div></div>
              <div><Label>Para Birimi</Label>
                <Select value={org.currency} onValueChange={(v) => setOrg({ ...org, currency: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="org-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRY">₺ TRY</SelectItem>
                    <SelectItem value="USD">$ USD</SelectItem>
                    <SelectItem value="EUR">€ EUR</SelectItem>
                    <SelectItem value="GBP">£ GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">Saat dilimi: Europe/Istanbul • Tarih: GG.AA.YYYY</div>
            {can("manage_settings") && <Button onClick={saveOrg} data-testid="org-save-button">Kaydet</Button>}
          </Card>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="flex justify-end mb-3">{can("manage_users") && <Button onClick={() => setInviteOpen(true)} data-testid="invite-user-button"><Plus className="h-4 w-4 mr-1.5" /> Kullanıcı Ekle</Button>}</div>
          <Card className="divide-y">
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-3 p-3.5" data-testid={`member-${m.id}`}>
                <Avatar className="h-9 w-9"><AvatarFallback className="text-xs">{initials(m.name)}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{m.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                  {m.allowed_modules != null && m.role !== "owner" && m.role !== "admin" && (
                    <p className="text-[11px] text-indigo-600 mt-0.5">Özel erişim: {m.modules?.length || 0} bölüm</p>
                  )}
                </div>
                {manageAccess && m.role !== "owner" && m.role !== "admin" && m.id !== user?.id && (
                  <Button variant="outline" size="sm" onClick={() => openAccess(m)} data-testid={`access-btn-${m.id}`}>
                    <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" /> Erişim
                  </Button>
                )}
                {can("manage_users") && m.role !== "owner" ? (
                  <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                    <SelectTrigger className="w-44" data-testid={`role-select-${m.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <Badge variant="secondary">{m.role_label}</Badge>}
                {can("manage_users") && m.role !== "owner" && m.id !== user?.id && (
                  <Button variant="ghost" size="icon" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 shrink-0"
                    onClick={() => setRemoveTarget(m)} data-testid={`delete-member-${m.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="structure" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-5">
              <div className="flex items-center justify-between mb-3"><h3 className="font-semibold">Şubeler</h3>{can("manage_branches") && <Button size="sm" variant="outline" onClick={addBranch} data-testid="add-branch"><Plus className="h-4 w-4" /></Button>}</div>
              <div className="space-y-2">{branches.map((b) => <div key={b.id} className="text-sm p-2 rounded-lg bg-muted">{b.name}</div>)}{branches.length === 0 && <EmptyState title="Şube yok" />}</div>
            </Card>
            <Card className="p-5">
              <div className="flex items-center justify-between mb-3"><h3 className="font-semibold">Departmanlar</h3>{can("manage_departments") && <Button size="sm" variant="outline" onClick={addDept} data-testid="add-dept"><Plus className="h-4 w-4" /></Button>}</div>
              <div className="space-y-2">{departments.map((d) => <div key={d.id} className="text-sm p-2 rounded-lg bg-muted">{d.name}</div>)}{departments.length === 0 && <EmptyState title="Departman yok" />}</div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="ai" className="mt-4">          <Card className="p-5 max-w-lg">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="h-5 w-5 text-indigo-600" /><h3 className="font-semibold">AI İş Asistanı</h3><Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Aktif</Badge></div>
            <p className="text-sm text-muted-foreground">AI asistanı Emergent Universal Key ile sunucu tarafında çalışır. Doğal dilden görev oluşturma, metin özetleme gibi özellikleri Görevler ve Notlar bölümlerinde kullanabilirsiniz. API anahtarları asla istemci tarafında saklanmaz.</p>
          </Card>
        </TabsContent>

        {can("manage_workflows") && (
          <TabsContent value="automations" className="mt-4">
            <AutomationsTab />
          </TabsContent>
        )}

        <TabsContent value="audit" className="mt-4">
          <Card className="divide-y max-h-[500px] overflow-y-auto">
            {audit.length ? audit.map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-3 text-sm">
                <span className="font-medium">{a.user_name}</span>
                <span className="text-muted-foreground">{a.action}</span>
                <span className="truncate flex-1">{a.detail}</span>
                <span className="text-xs text-muted-foreground">{fromNow(a.created_at)}</span>
              </div>
            )) : <EmptyState icon={Shield} title="Denetim kaydı yok" />}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!accessUser} onOpenChange={(v) => !v && setAccessUser(null)}>
        <DialogContent data-testid="access-dialog">
          <DialogHeader><DialogTitle>Erişim Ayarları — {accessUser?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">Bu kullanıcının görebileceği bölümleri seçin. Seçilmeyen bölümler menüde gizlenir ve erişilemez.</p>
          <div className="grid grid-cols-2 gap-2.5 py-2 max-h-[50vh] overflow-y-auto">
            {MODULE_CATALOG.map((mod) => {
              const locked = mod.key === "dashboard";
              const checked = locked || accessMods.includes(mod.key);
              return (
                <label key={mod.key} className={`flex items-center gap-2.5 rounded-lg border p-2.5 text-sm ${locked ? "opacity-60" : "cursor-pointer hover:bg-muted/50"}`} data-testid={`access-mod-${mod.key}`}>
                  <Checkbox checked={checked} disabled={locked} onCheckedChange={() => !locked && toggleMod(mod.key)} />
                  <span>{mod.label}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={resetAccess} className="sm:mr-auto" data-testid="access-reset-button"><RotateCcw className="h-4 w-4 mr-1.5" /> Rol varsayılanına dön</Button>
            <Button variant="outline" onClick={() => setAccessUser(null)}>İptal</Button>
            <Button onClick={saveAccess} data-testid="access-save-button">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader><DialogTitle>Kullanıcı Ekle</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Ad Soyad</Label><Input value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} className="mt-1.5" data-testid="invite-name-input" /></div>
            <div><Label>E-posta</Label><Input value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="mt-1.5" data-testid="invite-email-input" /></div>
            <div><Label>Rol</Label>
              <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.filter((r) => r.id !== "owner").map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">Varsayılan şifre: WorkHub2026!</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setInviteOpen(false)}>İptal</Button><Button onClick={doInvite} data-testid="invite-save-button">Ekle</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!removeTarget} onOpenChange={(v) => !v && setRemoveTarget(null)}>
        <AlertDialogContent data-testid="delete-member-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Kullanıcıyı sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{removeTarget?.name}</strong> kuruluştan çıkarılacak ve görev atamalarından kaldırılacak.
              Başka bir kuruluşta üyeliği yoksa hesabı tamamen silinir. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={deleteMember} data-testid="delete-member-confirm">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
